import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import LaserBackground from '../components/LaserBackground';
import '../buildForMe.css';
import '../startProject.css';

const SERVICES = [
  'Custom Website Design',
  'E-Commerce Solutions',
  'POS & Inventory Systems',
  'Web Apps & Software',
  'Database Architecture',
  'Custom AI Tools & Automation',
];

// ── Helpers ───────────────────────────────────────────────
function formatPhone(raw) {
  const digits = raw.replace(/\D/g, '');
  const main = digits.slice(0, 10);
  const ext  = digits.slice(10, 16);
  let out = '';
  if (main.length === 0) return '';
  if (main.length <= 3)  out = `(${main}`;
  else if (main.length <= 6) out = `(${main.slice(0,3)}) ${main.slice(3)}`;
  else out = `(${main.slice(0,3)}) ${main.slice(3,6)}-${main.slice(6)}`;
  if (ext.length) out += ` ext. ${ext}`;
  return out;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

// ── Profile card ──────────────────────────────────────────
function ProfileCard({ service, onSubmit, submitting }) {
  const [form, setForm] = useState({ name: '', date: '', budget: '', email: '', phone: '' });
  const [emailTouched, setEmailTouched] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handlePhone = (e) => {
    setForm((p) => ({ ...p, phone: formatPhone(e.target.value) }));
  };

  const emailValid = !form.email.trim() || EMAIL_RE.test(form.email.trim());
  const canSubmit  = form.name.trim()
    && (form.email.trim() || form.phone.trim())
    && emailValid;

  return (
    <div className="sp-profile">
      <div className="sp-profile-service">{service}</div>
      <p className="sp-profile-intro">Tell us a bit about yourself and your project.</p>
      <div className="sp-profile-fields">
        <input className="sp-gate-input" placeholder="Your name" value={form.name} onChange={set('name')} autoFocus />
        <input className="sp-gate-input" placeholder="Desired completion date" value={form.date} onChange={set('date')} />
        <input className="sp-gate-input" placeholder="Approximate budget (optional)" value={form.budget} onChange={set('budget')} />
        <div className="sp-profile-contact-head">
          Best way to reach you <span className="sp-profile-hint">— at least one required</span>
        </div>
        <input
          className={`sp-gate-input${emailTouched && !emailValid ? ' sp-gate-input--error' : ''}`}
          type="email"
          placeholder="Email address"
          value={form.email}
          onChange={set('email')}
          onBlur={() => setEmailTouched(true)}
        />
        {emailTouched && !emailValid && (
          <div className="sp-gate-field-error">Enter a valid email (e.g. name@domain.com)</div>
        )}
        <input
          className="sp-gate-input"
          type="tel"
          placeholder="Phone  (xxx) xxx-xxxx  ext. optional"
          value={form.phone}
          onChange={handlePhone}
        />
      </div>
      <button
        className="sp-gate-btn"
        disabled={!canSubmit || submitting}
        onClick={() => onSubmit(form)}
      >
        {submitting ? 'Sending…' : 'Submit →'}
      </button>
    </div>
  );
}

// ── Thank you ─────────────────────────────────────────────
function ThankYou({ name, email, phone, service }) {
  const firstName = name ? name.split(' ')[0] : name;
  const contact = [email, phone].filter(Boolean).join('  ·  ');
  return (
    <div className="sp-done">
      <div className="sp-done-mark">✓</div>
      <p className="sp-done-title">Thank you, {firstName}.</p>
      <p className="sp-done-sub">
        Your {service} inquiry has been forwarded to our design team.
        We’ll reach out to <span className="sp-done-contact-inline">{contact}</span> within one business day.
      </p>
      <p className="sp-done-forwarded">Forwarded to designteam@ramdesignworks.com</p>
      <Link to="/" className="sp-done-back">← Back to RAMDESIGNWORKS</Link>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────





// ── Main ──────────────────────────────────────────────────
export default function StartProject() {
  const isPreview = new URLSearchParams(window.location.search).get('done') === '1';
  const [phase, setPhase]                     = useState(isPreview ? 'done' : 'select');
  const [fading, setFading]                   = useState(false);
  const [selectedService, setSelectedService] = useState(isPreview ? 'Custom Website Design' : null);
  const [submitting, setSubmitting]           = useState(false);
  const [pageIn, setPageIn]                   = useState(false);
  const [submittedData, setSubmittedData]     = useState(
    isPreview ? { name: 'Preview User', email: 'preview@test.com', phone: '555-0100' } : null
  );
  const [sessionId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.add('mirror-body');
    setTimeout(() => setPageIn(true), 60);
    return () => document.body.classList.remove('mirror-body');
  }, []);

  const fadeSwap = (fn) => {
    setFading(true);
    setTimeout(() => { fn(); setFading(false); }, 300);
  };

  const handleServiceSelect = (svc) => {
    setSelectedService(svc);
    setTimeout(() => fadeSwap(() => setPhase('profile')), 700);
  };

  const handleProfileSubmit = async (formData) => {
    setSubmitting(true);
    try {
      const fn = httpsCallable(getFunctions(), 'captureProjectLead');
      await fn({ ...formData, service: selectedService, sessionId });
    } catch (e) {
      console.error('captureProjectLead error:', e);
    }
    setSubmittedData(formData);
    setSubmitting(false);
    fadeSwap(() => setPhase('done'));
  };

  return (
    <div className={`bfm-page ${pageIn ? 'bfm-page-in' : ''}`}>

      <LaserBackground />

      <div className="bfm-brand-blast">
        <div className="bfm-steel-bar">
          <div className="bfm-twinkle bfm-t1" />
          <div className="bfm-twinkle bfm-t2" />
          <div className="bfm-twinkle bfm-t3" />
          <div className="bfm-twinkle bfm-t4" />
          <div className="bfm-twinkle bfm-t5" />
        </div>
        <div className="bfm-brand-name">RAMDESIGNWORKS</div>
        <div className="bfm-brand-subtitle">AI&#8209;Powered Professional Services</div>
        <div className="bfm-brand-rule" />
        <Link to="/" className="mirror-back-nav">&#8592; Back</Link>
      </div>

      <div className="sp-content">
        <div className="sp-layout">

          {/* ── Services sidebar ── */}
          <aside className="sp-sidebar">
            <div className="sp-sidebar-label">What We Build</div>
            <ul className="sp-svc-list">
              {SERVICES
                .filter((svc) => !selectedService || svc === selectedService)
                .map((svc) => (
                  <li key={svc}>
                    <button
                      className={`sp-svc-btn${selectedService === svc ? ' sp-svc-btn--selected' : ''}`}
                      disabled={!!selectedService}
                      onClick={() => handleServiceSelect(svc)}
                    >
                      {svc}
                    </button>
                  </li>
                ))}
            </ul>
          </aside>

          {/* ── Chat panel ── */}
          <div className="sp-chat-panel">
            <div className={`sp-chat-inner${fading ? ' sp-fading' : ''}`}>

              {phase === 'select' && (
                <div className="sp-messages">
                  <div className="sp-msg sp-msg-bot">
                    <div className="sp-msg-avatar">RDW</div>
                    <div className="sp-msg-bubble">
                      {selectedService
                        ? `${selectedService} \u2014 great choice. One moment\u2026`
                        : 'Select a service from the left that best fits your project.'}
                    </div>
                  </div>
                </div>
              )}

              {phase === 'profile' && (
                <ProfileCard
                  service={selectedService}
                  onSubmit={handleProfileSubmit}
                  submitting={submitting}
                />
              )}

              {phase === 'done' && submittedData && (
                <ThankYou
                  name={submittedData.name}
                  email={submittedData.email}
                  phone={submittedData.phone}
                  service={selectedService}
                />
              )}

            </div>
          </div>{/* end sp-chat-panel */}

        </div>{/* end sp-layout */}
      </div>

      <footer className="bfm-footer">
        <div className="bfm-footer-row1">
          <span className="bfm-footer-cta-sub">Reach us directly &middot; one business day response</span>
          <a
            href="mailto:info@ramdesignworks.com?subject=Project%20Inquiry%20%E2%80%94%20RAMDESIGNWORKS"
            className="bfm-footer-cta-email"
          >
            info@ramdesignworks.com
          </a>
        </div>
        <div className="bfm-footer-brand">
          R&nbsp;A&nbsp;M&nbsp;D&nbsp;E&nbsp;S&nbsp;I&nbsp;G&nbsp;N&nbsp;W&nbsp;O&nbsp;R&nbsp;K&nbsp;S&nbsp;&#8482;
        </div>
      </footer>

    </div>
  );
}
